import { ChangeEvent, FC, ReactElement, ReactNode, RefObject, useRef } from 'react';

import { Tooltip } from '@pp/core/src/components/tooltip/Tooltip';
import { useModifiers } from '@pp/core/src/styles/hooks/useModifiers';

import { BetInput } from '../betInput/BetInput';

import styles from './BetStepper.module.css';

export interface BetStepperProps {
  inputRef?: RefObject<HTMLInputElement>;
  prefixIcon: ReactNode;
  postfixIcon: ReactNode;
  betFieldValue: string;
  inputLabel?: ReactElement;
  tooltipText?: string;
  onStepperDecrement?: () => void;
  onStepperIncrement?: () => void;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  isEditing?: boolean;
  isDisabled?: boolean;
  isIncrementDisabled?: boolean;
  isDecrementDisabled?: boolean;
  forceTriggerTooltip?: boolean;
  customInputFontSize?: number;
  attrs?: string[];
}
export const BetStepper: FC<BetStepperProps> = ({
  inputRef,
  prefixIcon,
  postfixIcon,
  betFieldValue,
  inputLabel = '',
  tooltipText = '',
  onStepperDecrement,
  onStepperIncrement,
  onChange,
  isEditing = false,
  isDisabled = false,
  isDecrementDisabled = false,
  isIncrementDisabled = false,
  forceTriggerTooltip = false,
  customInputFontSize = 0,
  attrs,
}): ReactElement | null => {
  const prefixIconRef = useRef<HTMLDivElement>(null);
  const postfixIconRef = useRef<HTMLDivElement>(null);
  const mods = useModifiers(styles, 'bet_stepper', {
    disabled: isDisabled,
  });
  const mods_decrement = useModifiers(styles, 'prefix_icon', {
    enabled: !isDecrementDisabled,
  });
  const mods_increment = useModifiers(styles, 'postfix_icon', {
    enabled: !isIncrementDisabled,
  });
  const mods_inputArea = useModifiers(styles, 'input_area', {
    editing: isEditing,
  });

  return (
    <div className={`${styles.bet_stepper} ${mods}`}>
      <button className={`${styles.prefix_icon} ${mods_decrement}`} onClick={onStepperDecrement}>
        <Tooltip text={'REDUCE'} targetRef={prefixIconRef} />
        <div ref={prefixIconRef} className={styles.icon_child}>
          {prefixIcon}
        </div>
      </button>
      <BetInput
        inputRef={inputRef}
        containerClass={`${styles.input_area} ${mods_inputArea}`}
        label={inputLabel}
        value={betFieldValue}
        onChange={onChange}
        tooltipMsg={tooltipText}
        isEditing={isEditing}
        forceTriggerTooltip={forceTriggerTooltip}
        isDisabled={isDisabled}
        inputCSSOverride={styles.bet_input_ovr}
        customInputFontSize={customInputFontSize}
        attrs={attrs}
      />
      <button className={`${styles.postfix_icon} ${mods_increment}`} onClick={onStepperIncrement}>
        <Tooltip text={'INCREASE'} targetRef={postfixIconRef} />
        <div ref={postfixIconRef} className={styles.icon_child}>
          {postfixIcon}
        </div>
      </button>
    </div>
  );
};
